import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { LetDirective } from '@ngrx/component';
import { isEqual } from 'lodash-es';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import {
  DashboardAnomaly,
  DashboardDevice,
  DashboardInstallationData,
  SubscriptionPlan,
} from '@vi-iot/client';

import {
  ACTIVE_ERROR_TYPE,
  KanbanBoardStore,
  QuickViewErrorDetails,
  QuickViewSmartNotes,
} from '@store';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RouterLinkWithHref } from '@angular/router';
import { PillComponent } from '@components';
import { RemoteServiceService } from '../../../+installations/installation/remote-service/remote-service.service';
import { PillType } from '../../../shared/components/pill/pill.model';
import { KanbanQuickViewHeaderComponent } from './kanban-quick-view-header/kanban-quick-view-header.component';
import { QuickViewAnomaly, QuickViewHint } from './kanban-quick-view-wrapper.helper';

import { ViTrustHtmlPipe } from '../../../../projects/ui/pipes/trust-html.pipe';
import { CustomTranslationsService } from '../../../operational-data-common/translations';
import { FormatPhoneNumberPipe } from './../../../shared/pipes/format-phone-number/format-phone-number.pipe';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GtmDirective } from 'src/app/shared/directives/gtm/gtm.directive';
import { HighestSubscriptionPlanPipe } from '../pipes/highest-subscription-plan/highest-subscription-plan.pipe';
import { RemoteServiceMapper } from 'src/app/@store/remote-service/remote-service.mapper';
import { DashboardAnomalyPillHelper } from '../pipes/dashboard-anomaly-pill/dashboard-anomaly-pill.helper';

@Component({
  selector: 'app-kanban-quick-view-wrapper',
  standalone: true,
  templateUrl: './kanban-quick-view-wrapper.component.html',
  styleUrls: ['./kanban-quick-view-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    GtmDirective,
    TranslateModule,
    MatIconModule,
    PillComponent,
    MatProgressSpinnerModule,
    KanbanQuickViewHeaderComponent,
    ViTrustHtmlPipe,
    FormatPhoneNumberPipe,
    RouterLinkWithHref,
    MatButtonModule,
    LetDirective,
    HighestSubscriptionPlanPipe,
  ],
})
export class KanbanQuickViewWrapperComponent implements OnInit, OnDestroy {
  readonly pillType = PillType;
  readonly defaultNumberOfVisibleErrors = 3;
  readonly SubscriptionPlan = SubscriptionPlan;

  installation: DashboardInstallationData;
  anomalies$: Observable<QuickViewAnomaly[]>;
  hints$: Observable<QuickViewHint[]>;
  errors$: Observable<QuickViewErrorDetails[]>;
  selectedDevice: DashboardDevice;
  isShowingMoreSystemStatuses = false;

  private onDestroy$: Subject<void> = new Subject();

  constructor(
    public kanbanBoardStore: KanbanBoardStore,
    public remoteServiceService: RemoteServiceService,
    private customTranslationsService: CustomTranslationsService,
    private translateService: TranslateService
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  ngOnInit(): void {
    this.hints$ = this.kanbanBoardStore.quickViewHints$.pipe(
      distinctUntilChanged(isEqual),
      map((hints) => this.mapHints(hints))
    );

    this.kanbanBoardStore.quickViewInstallation$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((installation) => {
        this.installation = installation;
      });

    this.kanbanBoardStore.quickViewSelectedDevice$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((selectedDevice) => {
        this.selectedDevice = selectedDevice;
      });

    this.anomalies$ = this.kanbanBoardStore.quickViewAnomalies$.pipe(
      map((anomalies) => this.anomaliesMapper(anomalies))
    );
    this.errors$ = this.kanbanBoardStore.quickViewErrorsForSpecificDevice$;
  }

  private anomaliesMapper(input: DashboardAnomaly[]): QuickViewAnomaly[] {
    return input.map((anomaly) => {
      const pillType = DashboardAnomalyPillHelper.getAnomalyPillType(anomaly);

      return {
        header: this.remoteServiceService.getRemoteServicePrefix(anomaly.diagnosisName),
        description: '',
        // Uncomment when backend is ready
        // description: this.remoteServiceService.getRemoteServicePrefix(anomaly.diagnosisText),
        pillType,
        pillText: RemoteServiceMapper.formatDiagnosisId(anomaly.id),
        severity: anomaly.severity,
      };
    });
  }

  closeOverlay(): void {
    this.kanbanBoardStore.forceQuickViewClose$.next();
  }

  toggleErrorsVisibility(value: boolean): void {
    this.isShowingMoreSystemStatuses = value;
  }

  private mapHints(smartNotes: QuickViewSmartNotes[]): QuickViewHint[] {
    const hints: QuickViewHint[] = [];

    smartNotes.forEach((smartNote) => {
      switch (smartNote.type) {
        case ACTIVE_ERROR_TYPE.DEVICE_BATTERY_LEVEL_LOW:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.DEVICE_BATTERY_LEVEL_LOW
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.SALT_CARTRIDGE_EXCHANGE:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.SALT_CARTRIDGE_EXCHANGE
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.MAINTENANCE_TIMEBASED:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.MAINTENANCE_TIMEBASED
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.MAINTENANCE_BURNERBASED:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.MAINTENANCE_BURNERBASED
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.FILTER_EXCHANGE:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.FILTER_EXCHANGE
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_30_D
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.MAINTENANCE_NEEDED_IN_45_D
              ]
            ),
            status: 'maintenance',
          });
          break;
        case ACTIVE_ERROR_TYPE.FILTER_EXCHANGE_14_DAYS:
          hints.push({
            header: this.translateService.instant(
              this.customTranslationsService?.MAINTENANCE_NOTIFICATIONS[
                ACTIVE_ERROR_TYPE.FILTER_EXCHANGE_14_DAYS
              ]
            ),
            status: 'maintenance',
          });
          break;
      }
    });

    return hints;
  }

  manageStatusesListSize(value: boolean): void {
    this.isShowingMoreSystemStatuses = value;
  }
}
